/**
 * String → parent binding
 *
 * A bare in-table string number ("17") classifies as string level but knows
 * nothing above itself — and the same number repeats in every table. This
 * pass runs after (re)classification and stamps each parentless string
 * entity with the hierarchy of its nearest parent label (the DCCB heading
 * that governs its table), in PDF sheet coordinates.
 *
 * The binding is recorded via `parentEntityId` so it is inspectable and
 * re-derivable; a radius cap (adaptive: 3× the median nearest-parent
 * distance) keeps stray numbers in title blocks from binding to a parent
 * half a sheet away.
 */

import RBush from 'rbush';
import knn from 'rbush-knn';
import type { LayoutTextEntity, TopologyFields, ClassificationPattern } from './types';
import { reclassifyEntities, effectiveClassification, type ClassifiedEntity } from './classify';

/** Parent levels by depth — bind to the deepest level present on the sheet. */
const PARENT_LEVEL_PRIORITY = ['dccb', 'mppt', 'inverter', 'station'] as const;

/** A string entity already carrying any of these fields is not parentless. */
const PARENT_FIELDS: Array<keyof TopologyFields> = ['dccb', 'mppt', 'inverter', 'station'];

interface ParentItem {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  entity: LayoutTextEntity;
}

export interface ParentBindResult {
  entities: LayoutTextEntity[];
  /** How many string entities received an inferred parent */
  boundCount: number;
  /** Bound strings per parent entity id — feeds the stringCount audit */
  perParent: Map<string, number>;
}

/**
 * Bind parentless string-level entities to their nearest parent label.
 * Entities with a manual override are never modified.
 */
export function bindStringParents(entities: LayoutTextEntity[]): ParentBindResult {
  // 1. Choose the parent level: deepest priority level with enough presence
  let parentLevel: string | undefined;
  for (const level of PARENT_LEVEL_PRIORITY) {
    if (entities.some((e) => effectiveClassification(e)?.level === level)) {
      parentLevel = level;
      break;
    }
  }
  if (!parentLevel) {
    return { entities, boundCount: 0, perParent: new Map() };
  }

  // 2. Spatial index of parent labels (PDF point space)
  const items: ParentItem[] = [];
  for (const e of entities) {
    if (effectiveClassification(e)?.level !== parentLevel) continue;
    items.push({ minX: e.cx, maxX: e.cx, minY: e.cy, maxY: e.cy, entity: e });
  }
  if (items.length === 0) {
    return { entities, boundCount: 0, perParent: new Map() };
  }
  const tree = new RBush<ParentItem>();
  tree.load(items);

  // 3. First pass: nearest-parent distance for every parentless string
  interface Candidate {
    index: number;
    parent: LayoutTextEntity;
    distance: number;
  }
  const candidates: Candidate[] = [];

  entities.forEach((e, index) => {
    if (e.classificationOverride) return; // manual override — hands off
    const cls = e.classification;
    if (!cls || cls.level !== 'string') return;
    const h = cls.hierarchy;
    if (h && PARENT_FIELDS.some((f) => h[f])) return; // already has lineage

    const nearest = knn(tree, e.cx, e.cy, 1) as ParentItem[];
    if (nearest.length === 0) return;
    const p = nearest[0];
    const distance = Math.hypot(p.entity.cx - e.cx, p.entity.cy - e.cy);
    candidates.push({ index, parent: p.entity, distance });
  });

  if (candidates.length === 0) {
    return { entities, boundCount: 0, perParent: new Map() };
  }

  // 4. Adaptive radius cap: 3× the median nearest-parent distance.
  //    In-table numbers sit within a table pitch of their heading; title-
  //    block strays are far outliers and stay unbound.
  const sorted = candidates.map((c) => c.distance).sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const cap = median * 3;

  // 5. Second pass: stamp hierarchies
  const out = entities.slice();
  const perParent = new Map<string, number>();
  let boundCount = 0;

  for (const c of candidates) {
    if (c.distance > cap) continue;
    const parentCls = effectiveClassification(c.parent);
    if (!parentCls) continue;

    const parentHierarchy: Partial<TopologyFields> = {};
    if (parentCls.hierarchy) {
      for (const f of PARENT_FIELDS) {
        if (parentCls.hierarchy[f]) parentHierarchy[f] = parentCls.hierarchy[f];
      }
    }
    // Parent classified without captures (level:value only) — use its value
    if (Object.keys(parentHierarchy).length === 0) {
      (parentHierarchy as Record<string, string>)[parentLevel] = parentCls.value;
    }

    const entity = out[c.index];
    const cls = entity.classification!;
    out[c.index] = {
      ...entity,
      classification: {
        ...cls,
        hierarchy: { ...parentHierarchy, ...cls.hierarchy },
        parentEntityId: c.parent.id,
      },
    };
    boundCount++;
    perParent.set(c.parent.id, (perParent.get(c.parent.id) || 0) + 1);
  }

  return { entities: out, boundCount, perParent };
}

/**
 * The canonical re-classification step: classify with the given patterns,
 * then bind parentless strings to their nearest parent label. Every apply
 * flow (families panel, schema panel, patterns modal, apply-to-all-layouts)
 * goes through this so lineage stamping cannot be forgotten.
 */
export function reclassifyAndBind(
  entities: LayoutTextEntity[],
  customPatterns?: ClassificationPattern[],
): LayoutTextEntity[] {
  const reclassified = reclassifyEntities(
    entities as ClassifiedEntity[],
    customPatterns,
  ) as unknown as LayoutTextEntity[];
  return bindStringParents(reclassified).entities;
}
